import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  CircularProgress,
  Typography 
} from '@mui/material';
import * as SecureStore from 'expo-secure-store';
import './consoles.css';
import AddConsoleModal from './AddConsoleModal';

const REACT_APP_API_URL = 'https://gamecollection-app.onrender.com/api';
const REACT_APP_API = 'https://gamecollection-app.onrender.com';

export default function Consoles() {
  const [consoles, setConsoles] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [boxArts, setBoxArts] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [selectedConsole, setSelectedConsole] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [locked, setLocked] = useState(true);
  const [loadingData, setLoadingData] = useState(true);

  // --- STORAGE & UNLOCK LOGIC ---
  const sendUnlock = async (passwordOrToken) => {
    try {
      const response = await axios.post(`${REACT_APP_API_URL}/unlock`, { password: passwordOrToken });
      if (response.data.unlocked) {
        setLocked(false);
        sessionStorage.setItem("token", passwordOrToken);
        
        try {
          await SecureStore.setItemAsync("myToken", response.data.token);
        } catch (e) {
          console.log("SecureStore not available on web");
        }
      }
    } catch (error) {
      console.log("Password check failed");
    }
  };

  const fetchData = async () => {
    setLoadingData(true);
    try {
      const [consolesResult, boxArtsResult, platformsResult] = await Promise.allSettled([
        axios.get(`${REACT_APP_API_URL}/consoles`),
        axios.post(`${REACT_APP_API_URL}/console/boxarts`),
        axios.get(`${REACT_APP_API_URL}/platforms`),
      ]);
      if (consolesResult.status === 'rejected') {
        throw consolesResult.reason;
      }
      const resConsoles = consolesResult.value;
      const sortedConsoles = [...resConsoles.data].sort((a, b) =>
        String(a?.name || '').localeCompare(String(b?.name || ''), undefined, {
          sensitivity: 'base',
          numeric: true,
        })
      );
      setConsoles(sortedConsoles);
      if (boxArtsResult.status === 'fulfilled') {
        setBoxArts(boxArtsResult.value.data);
      } else {
        console.error('Console artwork fetch failed', boxArtsResult.reason);
      }
      if (platformsResult.status === 'fulfilled') {
        setPlatforms(platformsResult.value.data);
      } else {
        console.error('Platform list fetch failed', platformsResult.reason);
      }
    } catch (e) {
      console.error("Data fetch failed", e);
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        const sessionToken = sessionStorage.getItem("token");
        if (sessionToken) {
          await sendUnlock(sessionToken);
        }

        const token = await SecureStore.getItemAsync("myToken");
        if (token) {
          await sendUnlock(token);
        }
      } catch (e) {
        console.log("No token found or storage unavailable");
      }
      fetchData();
    };
    init();
  }, []);

  const boxArtByConsoleId = useMemo(
    () => new Map(boxArts.map((art) => [String(art.id), art.url])),
    [boxArts]
  );

  const getBoxArtUrl = (consoleId) => {
    const url = boxArtByConsoleId.get(String(consoleId));
    if (!url) return null;
    if (/^https?:\/\//i.test(url)) return url;
    return `${REACT_APP_API}/${url.replace(/^\/+/, '')}`;
  };

  const handleBoxArtError = (event, consoleName) => {
    const image = event.currentTarget;
    if (image.dataset.fallback === 'default') return;

    if (!image.dataset.fallback) {
      image.dataset.fallback = 'console-logo';
      try {
        image.src = require(`./assets/old-console-logos/${consoleName.replace(/ /g, '_')}.png`);
        return;
      } catch (error) {
        // Use the generic fallback when this console has no bundled logo.
      }
    }

    image.dataset.fallback = 'default';
    image.src = require('./assets/old-console-logos/default.png');
  };

  const handleOpenEdit = (consoleObj) => {
    if (locked) return;
    setSelectedConsole(consoleObj);
    setEditOpen(true);
  };

  const handleCloseEdit = () => {
    setEditOpen(false);
    setSelectedConsole(null);
    setUploadFile(null);
  };

  const handleSave = async () => {
    if (!selectedConsole) return;
    setSaving(true);

    const formData = new FormData();
    formData.append("id", selectedConsole.id);
    formData.append("variation", selectedConsole.variation || "");
    formData.append("condition", selectedConsole.condition || "");
    formData.append("notes", selectedConsole.notes || "");
    if (uploadFile) {
      formData.append("boxart", uploadFile);
    }

    try {
      await axios.post(`${REACT_APP_API_URL}/console/edit`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      fetchData();
      handleCloseEdit();
    } catch (err) {
      console.error("Save failed", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="shelves-wrapper">
        <div className="shelves-container">
          {loadingData && consoles.length === 0 && (
            <div className="console-loading" role="status" aria-live="polite">
              <CircularProgress size={28} />
              <Typography>Loading consoles and artwork...</Typography>
            </div>
          )}
          {consoles.map((console) => (
            <div
              key={console.id}
              className="console-card"
              onClick={() => handleOpenEdit(console)}
            >
              <img
                src={getBoxArtUrl(console.id) || require(`./assets/old-console-logos/default.png`)}
                alt={console.name || 'Console'}
                className="console-image"
                loading="lazy"
                decoding="async"
                onError={(event) => handleBoxArtError(event, console.name || '')}
              />
              <div className="console-info">
                <h1><strong>{console.name}</strong></h1>
                <p><strong>Variation:</strong> {console.variation}</p>
                <p><strong>Condition:</strong> {console.condition}</p>
                {console.notes && <p><strong>Notes:</strong> {console.notes}</p>}
              </div>
            </div>
          ))}
          {!loadingData && consoles.length === 0 && (
          <Typography className="console-load-error" role="status">
            No consoles could be loaded. Please refresh to try again.
          </Typography>
          )}

          {/* ADD CONSOLE BUTTON AT END OF LIST */}
          {!locked && (
            <div 
              className="console-card add-card-shelf" 
              onClick={() => setAddModalOpen(true)}
              style={{ border: '2px dashed #666', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
            >
              <div style={{ textAlign: 'center', color: '#666' }}>
                <span style={{ fontSize: '3rem' }}>+</span>
                <p>Add Console</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <AddConsoleModal 
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onRefresh={fetchData}
        platforms={platforms}
      />

      {/* Edit Modal */}
      <Dialog open={editOpen} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Console</DialogTitle>
        <DialogContent dividers>
          {selectedConsole && (
            <>
              <TextField label="Variation" fullWidth margin="dense" value={selectedConsole.variation || ""} onChange={(e) => setSelectedConsole({ ...selectedConsole, variation: e.target.value })} />
              <TextField label="Condition" fullWidth margin="dense" value={selectedConsole.condition || ""} onChange={(e) => setSelectedConsole({ ...selectedConsole, condition: e.target.value })} />
              <TextField label="Notes" fullWidth margin="dense" multiline value={selectedConsole.notes || ""} onChange={(e) => setSelectedConsole({ ...selectedConsole, notes: e.target.value })} />
              <div style={{ marginTop: "15px" }}>
                <input type="file" accept="image/*" onChange={(e) => setUploadFile(e.target.files[0])} />
              </div>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>{saving ? "Saving..." : "Save"}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}